import { Box, Checkbox, Chip, TableCell, TableRow, Typography } from '@mui/material';
import { FC, memo, useMemo } from 'react';
import { TruckShiftEntity } from 'src/entities/TruckShift.entity';
import { BodyCellPresenter } from 'src/pages/TruckShifts/presenters/BodyCell.presenter';
import { mapShiftsToCells, ShiftCell } from 'src/pages/TruckShifts/truckShiftGrid';

type Props = {
  truck: TruckShiftEntity;
  dateColumns: string[];
  // selection オブジェクトではなく primitive で渡す（トグル毎に selection 全体の参照が変わり
  // 全行が再レンダーされる問題を回避するため。Component.tsx の useMemo(selection) 由来）。
  showCheckbox?: boolean;
  selected?: boolean;
  selectedDates?: Set<string>;
  onToggleTruck?: (truckId: number) => void;
  onCellClick?: (truckId: number, date: string, cells: ShiftCell[], anchorEl: HTMLElement) => void;
};

// selected=false の行は selectedDates が変わっても交差マーク（`selected && selectedDates.has(date)`）に
// 影響しないため、selectedDates の参照変化を無視して bail-out する（selected=true の行、または selected
// 自体が変わった行だけ selectedDates も比較して再レンダーする）。日付トグルは setSelectedDates が毎回
// 新しい Set を返すため、これが無いと数百行の BodyRow が全再レンダーされてしまう。
// 比較漏れを防ぐため Props の全フィールドを列挙して比較する。
export const bodyRowPropsAreEqual = (prev: Props, next: Props): boolean => {
  if (prev.truck !== next.truck) return false;
  if (prev.dateColumns !== next.dateColumns) return false;
  if ((prev.showCheckbox ?? false) !== (next.showCheckbox ?? false)) return false;
  const prevSelected = prev.selected ?? false;
  const nextSelected = next.selected ?? false;
  if (prevSelected !== nextSelected) return false;
  if (prev.onToggleTruck !== next.onToggleTruck) return false;
  if (prev.onCellClick !== next.onCellClick) return false;
  if (nextSelected && prev.selectedDates !== next.selectedDates) return false;
  return true;
};

export const BodyRowPresenter: FC<Props> = memo(({
  truck, dateColumns, showCheckbox = false, selected = false, selectedDates, onToggleTruck, onCellClick,
}) => {
  const cellsByDate = useMemo(() => mapShiftsToCells(truck.shifts, dateColumns), [truck.shifts, dateColumns]);
  // 削除済み（active=false）トラックは一括選択の対象外（誤って一括反映で新規稼働を作らないため）。
  // 個別のセル操作（追加/編集/割当）は従来どおり可能で、日付範囲はサーバー側 active_until で最終判定する。
  const selectable = showCheckbox && truck.active !== false;

  return (
    <TableRow hover>
      <TableCell
        onClick={selectable ? () => onToggleTruck?.(truck.id) : undefined}
        sx={{ width: 280, minWidth: 280, position: 'sticky', left: 0, backgroundColor: selected ? '#e3f2fd' : 'background.paper', zIndex: 1, borderRight: '1px solid', borderColor: 'divider', cursor: selectable ? 'pointer' : 'default' }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {showCheckbox && (
            <Checkbox
              size="small"
              sx={{ p: 0 }}
              checked={selected}
              disabled={truck.active === false}
              onChange={() => onToggleTruck?.(truck.id)}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                variant="body2"
                fontWeight={700}
                style={truck.active === false ? { textDecoration: 'line-through' } : undefined}
              >
                {truck.licensePlateValue}
              </Typography>
              {truck.klass && <Chip label={truck.klass} size="small" />}
            </Box>
            {/* 車番が空でも 1 行分の高さを確保し、車番あり/なしで通常ドライバー行の位置がズレないよう揃える */}
            <Typography variant="caption" color="text.secondary" component="div">{truck.truckNumber || ' '}</Typography>
            {/* 通常ドライバー行（Lovable 準拠）: あり=caption 既定色（明示 color を付けない＝Lovable と同じ）、
                なし=グレー＋斜体の muted 表示 */}
            <Typography
              variant="caption"
              component="div"
              sx={{
                fontWeight: 500,
                ...(truck.defaultDriver ? {} : { color: 'text.secondary', fontStyle: 'italic' }),
              }}
              style={truck.defaultDriver?.active === false ? { textDecoration: 'line-through' } : undefined}
            >
              通常: {truck.defaultDriver?.name ?? '未登録'}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      {dateColumns.map((date) => {
        const cells = cellsByDate[date] ?? [];
        // 列/行の全体塗りはせず、トラック選択×日付選択が重なるセルだけ選択枠を出す（Lovable 準拠）。
        const isIntersection = selected && (selectedDates?.has(date) ?? false);
        return (
          <TableCell
            key={date}
            data-intersection={isIntersection || undefined}
            onClick={onCellClick ? (e) => onCellClick(truck.id, date, cells, e.currentTarget) : undefined}
            sx={{
              // td に確定高さ(1px)を与えると、子 BodyCell の height:100% が「行の実レンダリング高」に解決し
              // セルが行高いっぱいに塗られる（td は行内の最も高いセルまで伸びるため実際は1pxにならない）。
              // height:100% では td 自身の高さが不定で子の % が解決せず、下に白余白が残る。
              p: 0, height: '1px', verticalAlign: 'top', width: 156, minWidth: 156, borderRight: '1px solid', borderColor: 'divider',
              cursor: onCellClick ? 'pointer' : 'default',
              // 交差セルのみ内側に選択枠（outline は子の背景に隠れず最前面に描画される）。
              outline: isIntersection ? '2px solid #1976d2' : undefined,
              outlineOffset: isIntersection ? '-2px' : undefined,
            }}
          >
            <BodyCellPresenter cells={cells} />
          </TableCell>
        );
      })}
    </TableRow>
  );
}, bodyRowPropsAreEqual);
